import dayjs from "dayjs";
import { useEffect, useRef } from "react";
import { DecimalHelper, StringHelper } from "@/helpers";
import {
  TransferRead,
  TransferHelper,
  TransferDepositStatusText,
  TransferClassHelper,
} from "@/domains/transfer";
import { useAppDispatch, useAppSelector } from "@/stores";
import { tenantMemberDepositSelects, tenantMemberDepositThunks } from "./store";
// components
import { Badge, Popconfirm, Table } from "antd";
import { ColumnsType } from "antd/es/table";
import {
  ArrowRightOutlined,
  ExportOutlined,
  EyeOutlined,
  IdcardOutlined,
  ShoppingOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { CopyIconButton } from "@/components";
import { TableTopFilters } from "./TableTopFilters";
import { CheckModal, CheckModalRef } from "./CheckModal";

export const TenantMemberDepositListPage = () => {
  const checkModalRef = useRef<CheckModalRef>(null);

  const dispatch = useAppDispatch();

  const loading = useAppSelector(tenantMemberDepositSelects.loading);

  const queryParams = useAppSelector(tenantMemberDepositSelects.queryParams);

  const transfers = useAppSelector(tenantMemberDepositSelects.transfers);

  useEffect(() => {
    void dispatch(tenantMemberDepositThunks.list(queryParams));
  }, [dispatch, queryParams]);

  const getRowClassName = (record: TransferRead) => {
    if (TransferHelper.isInvalid(record.status)) {
      return "fg:disabled v:top";
    }
    return "";
  };

  const handleCheckModalCheck = () => {
    void dispatch(tenantMemberDepositThunks.list(queryParams));
  };

  const columns: ColumnsType<TransferRead> = [
    {
      width: 150,
      title: "單號",
      dataIndex: "id",
    },
    {
      width: 260,
      className: "v:top",
      title: "平台單號 | 會員資料",
      key: "order",
      render(_, { order, member }) {
        return (
          <>
            <div className="word-break:break-all">
              <ShoppingOutlined className="mr:8" />
              {order?.id && StringHelper.getLength(order?.id) > 28 ? (
                <>
                  <span className="inline-block w:197 mr:8">
                    {StringHelper.ellipsisMiddle(order?.id, 28)}
                  </span>
                  <Popconfirm
                    title="平台單號"
                    description={
                      <>
                        <span className="mr:8 mb:8">{order?.id}</span>
                        <CopyIconButton text={order?.id} />
                      </>
                    }
                    showCancel={false}
                    okText="確定"
                    className="mr:8"
                  >
                    <ExportOutlined className="abs right:3 mt:4" />
                  </Popconfirm>
                </>
              ) : (
                <>
                  <span className="inline-block w:197 mr:8">
                    {order?.id ?? "?"}
                  </span>
                  {order?.id && <CopyIconButton text={order?.id} />}
                </>
              )}
            </div>
            <div className="word-break:break-all">
              <IdcardOutlined className="mr:8" />
              {member?.name && StringHelper.getLength(member?.name) > 28 ? (
                <>
                  <span className="inline-block w:175 mr:8">
                    {StringHelper.ellipsisEnd(member?.name, 28)}
                  </span>
                  <Popconfirm
                    title="會員名稱"
                    description={
                      <>
                        <span className="mr:8 mb:8">{member?.name}</span>
                        <CopyIconButton text={member?.name} />
                      </>
                    }
                    showCancel={false}
                    okText="確定"
                    className="mr:8"
                  >
                    <ExportOutlined />
                  </Popconfirm>
                </>
              ) : (
                <>
                  <span className="inline-block w:197 mr:8">
                    {`${member?.name ?? "?"}`}
                  </span>
                  {member?.name && <CopyIconButton text={member?.name} />}
                </>
              )}
            </div>
            <div className="word-break:break-all">
              <UserOutlined className="mr:8" />
              {member?.username &&
              StringHelper.getLength(member?.username) > 28 ? (
                <>
                  <span className="inline-block w:197 mr:8">
                    {StringHelper.ellipsisEnd(member?.username, 28)}
                  </span>
                  <Popconfirm
                    title="會員帳號"
                    description={
                      <>
                        <span className="mr:8 mb:8">{member?.username}</span>
                        <CopyIconButton text={member?.username} />
                      </>
                    }
                    showCancel={false}
                    okText="確定"
                    className="mr:8"
                  >
                    <ExportOutlined />
                  </Popconfirm>
                </>
              ) : (
                <>
                  <span className="inline-block w:197 mr:8">
                    {`${member?.username ?? "?"}`}
                  </span>
                  {member?.username && (
                    <CopyIconButton text={member?.username} />
                  )}
                </>
              )}
            </div>
          </>
        );
      },
    },
    {
      width: 280,
      key: "address",
      title() {
        return (
          <div className="flex gap:8">
            通道 | 匯率 | 打款地址 <ArrowRightOutlined /> 收款地址
          </div>
        );
      },
      render(_, { currency, rate, source, target }) {
        return (
          <div>
            {currency}
            {parseFloat(rate).toFixed(0) != "1" && (
              <Badge className="ml:8" count={rate} color="#53a168" />
            )}
            <div className="flex gap:8">
              {source?.address ? (
                <a
                  target="_blank"
                  href={TransferHelper.getAddressTransfersLink(source?.address)}
                >
                  {source?.address}
                </a>
              ) : (
                "?"
              )}
              <ArrowRightOutlined />
            </div>
            {target?.address ? (
              <a
                target="_blank"
                href={TransferHelper.getAddressTransfersLink(target?.address)}
              >
                {target?.address}
              </a>
            ) : (
              <></>
            )}
          </div>
        );
      },
    },
    {
      width: 90,
      title: "存單點數",
      key: "amount",
      sorter: () => 1,
      render(_, { currency, amount0, amount }) {
        return (
          <>
            <div className="t:right">{DecimalHelper.fixed(amount, 2)}</div>
            <div className="t:right">{currency}</div>
            {amount0 !== amount && (
              <div className="t:right fg:gray-58">
                {DecimalHelper.fixed(amount0, 2)}
              </div>
            )}
          </>
        );
      },
    },
    {
      width: 90,
      title: "實收點數",
      key: "result",
      sorter: () => 1,
      render(_, { currency, result0, result }) {
        return (
          result && (
            <>
              <div className="t:right">{DecimalHelper.fixed(result, 2)}</div>
              <div className="t:right">{currency}</div>
              {result0 !== result && (
                <div className="t:right fg:gray-58">
                  {DecimalHelper.fixed(result0, 2)}
                </div>
              )}
            </>
          )
        );
      },
    },
    {
      width: 70,
      title: "服務費",
      key: "charge",
      render(_, { currency, charge }) {
        return (
          charge && (
            <>
              <div className="t:right">{DecimalHelper.fixed(charge, 2)}</div>
              <div className="t:right">{currency}</div>
            </>
          )
        );
      },
    },
    {
      title: "備註",
      dataIndex: "note",
    },
    {
      width: 90,
      align: "right",
      className: "v:top px:6!:is(td)",
      title: "申請時間",
      key: "created_at",
      sorter: () => 1,
      render(_, { created_at }) {
        return <div>{dayjs.$ldts(created_at)}</div>;
      },
    },
    {
      width: 90,
      align: "right",
      className: "v:top px:6!:is(td)",
      title: "檢核時間",
      key: "checked_at",
      sorter: () => 1,
      render(_, { checked_at }) {
        return checked_at ? <div>{dayjs.$ldts(checked_at)}</div> : null;
      },
    },
    {
      width: 90,
      align: "right",
      className: "v:top px:6!:is(td)",
      title: "結算時間",
      key: "arrived_at",
      sorter: () => 1,
      render(_, { arrived_at }) {
        return arrived_at ? <div>{dayjs.$ldts(arrived_at)}</div> : null;
      },
    },
    {
      width: 140,
      title: "狀態",
      key: "status",
      render(_, record) {
        const TransactionEyeLink = ({ tran }: { tran: string }) => (
          <a
            className="ml:8"
            target="_blank"
            href={TransferHelper.getTransactionLink(tran)}
          >
            <EyeOutlined />
          </a>
        );
        return (
          <div>
            <span className={TransferClassHelper.getStatusColor(record.status)}>
              {TransferDepositStatusText[record.status]}
            </span>
            <span className="ml:8">
              {record.transaction && (
                <CopyIconButton text={record.transaction} />
              )}
            </span>
            {record.transaction &&
              record.transaction.split(",").map((tran, idx) => {
                return <TransactionEyeLink tran={tran} key={idx} />;
              })}
          </div>
        );
      },
    },
  ];

  return (
    <div className="flex flex:col">
      <TableTopFilters />

      <Table
        rowKey="id"
        tableLayout="fixed"
        loading={loading}
        columns={columns}
        dataSource={transfers}
        scroll={{ x: "1600px" }}
        rowClassName={getRowClassName}
      ></Table>

      <CheckModal ref={checkModalRef} onCheck={handleCheckModalCheck} />
    </div>
  );
};
